import React, { useState, useEffect, useRef } from "react";
import "./Number.css";
import { fromMmToUnit, fromUnitToMm } from "src/helpers/dimensions";
import { useDebouncedCallback } from "use-debounce";

interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  realMin?: number;
  step: number;
  label?: string;
  showSubLabel?: boolean;
  subStep?: number;
  className?: string;
  image?: string;
  unit?: "mm" | "cm";
  inputVersion?: string;
  additionalLabel?: string;
  disabled?: boolean;
  disabledMin?: boolean;
  disabledMax?: boolean;
  mask?: number;
}

const CLICK_DEBOUNCE_MS = 300;
const KEYBOARD_DEBOUNCE_MS = 2000;

export const NumberInput: React.FC<NumberInputProps> = ({
  image,
  value,
  onChange,
  min,
  max,
  step,
  label,
  showSubLabel = true,
  subStep,
  className,
  unit = "mm",
  inputVersion,
  additionalLabel,
  disabled = false,
  disabledMin = false,
  disabledMax = false,
  mask = 0,
  realMin = 0,
}) => {
  const displayValue = fromMmToUnit(value - (mask ?? 0), unit);
  const initialDisplay = displayValue.toString();
  const [inputValue, setInputValue] = useState<string>(initialDisplay);
  const [debouncedValue, setDebouncedValue] =
    useState<string>(initialDisplay);
  const [initialize, setInitialize] = React.useState(false);
  const [isNotValid, setIsNotValid] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputValueRef = useRef(inputValue);
  const ref = useRef<HTMLInputElement>(null);

  const debounceFromKeyboard = useDebouncedCallback(
    (nextValue: string) => {
      setDebouncedValue(nextValue);
    },
    KEYBOARD_DEBOUNCE_MS
  );

  const debounceFromClick = useDebouncedCallback(
    (nextValue: string) => {
      setDebouncedValue(nextValue);
    },
    CLICK_DEBOUNCE_MS
  );

  const scheduleKeyboardDebounce = (nextValue: string) => {
    debounceFromClick.cancel();
    debounceFromKeyboard(nextValue);
  };

  const scheduleClickDebounce = (nextValue: string) => {
    debounceFromKeyboard.cancel();
    debounceFromClick(nextValue);
  };

  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  useEffect(() => {
    return () => {
      debounceFromKeyboard.cancel();
      debounceFromClick.cancel();
    };
  }, [debounceFromKeyboard, debounceFromClick]);

  const displayedMin = fromMmToUnit(min - mask, unit);
  const displayedMax = fromMmToUnit(max - mask, unit);
  const stepForInput = fromMmToUnit(step, unit);
  const subStepForInput = fromMmToUnit(subStep ?? 0, unit);

  const current =
    inputValue === "" ? displayValue : parseInt(inputValue, 10);
  const atMin = isNaN(current) || current <= displayedMin;
  const atMax = isNaN(current) || current >= displayedMax;
  const atSubMin =
    isNaN(current) ||
    current - (subStepForInput ?? stepForInput) < displayedMin;
  const atSubMax =
    isNaN(current) ||
    current + (subStepForInput ?? stepForInput) > displayedMax;

  useEffect(() => {
    if (ref && ref.current) {
      const nextDisplay = displayValue.toString();
      ref.current.dispatchEvent(new Event("blur", { bubbles: true }));
      ref.current.value = nextDisplay;
      setInputValue(nextDisplay);

      if (inputValueRef.current !== nextDisplay) {
        scheduleClickDebounce(nextDisplay);
      }
    }
  }, [inputVersion]);

  useEffect(() => {
    setIsNotValid(false);

    if (isFocused && debouncedValue === "") {
      return;
    }

    if (!initialize) {
      setInitialize(true);
    } else {
      const parsedValue = parseInt(debouncedValue, 10);

      const parsedValueMm = isNaN(parsedValue)
        ? NaN
        : fromUnitToMm(parsedValue, unit);
      const realMm = isNaN(parsedValueMm) ? NaN : parsedValueMm + (mask ?? 0);

      const lowerMm = Math.floor(Math.max(min, realMin));
      const upperMm = Math.floor(max);

      if (!isNaN(parsedValue) && !isNaN(realMm)) {
        if (realMm >= lowerMm && realMm <= upperMm) {
          const normalized = parsedValue.toString();
          handleChange(parsedValue);
          setInputValue(normalized);

          if (normalized !== debouncedValue) {
            scheduleClickDebounce(normalized);
          }
          setIsNotValid(false);
        } else {
          const clampedMm = Math.min(Math.max(realMm, lowerMm), upperMm);
          const displayToApply = fromMmToUnit(clampedMm - (mask ?? 0), unit);
          const nextDisplay = displayToApply.toString();
          handleChange(displayToApply);
          setInputValue(nextDisplay);

          if (nextDisplay !== debouncedValue) {
            scheduleClickDebounce(nextDisplay);
          }
          setIsNotValid(true);
        }
      } else {
        const previousDisplay = fromMmToUnit(value - (mask ?? 0), unit);
        const nextDisplay = previousDisplay.toString();
        setInputValue(nextDisplay);

        if (nextDisplay !== debouncedValue) {
          scheduleClickDebounce(nextDisplay);
        }
        setIsNotValid(true);
      }
    }
  }, [debouncedValue]);

  const roundValue = (num: number, numStep: number) => {
    if (displayedMin !== undefined && num < displayedMin) {
      num = displayedMin;
    } else if (displayedMax !== undefined && num > displayedMax) {
      num = displayedMax;
    }

    return Math.round(num / numStep) * numStep;
  };

  const getCurrentValue = () => {
    if (inputValue === "") {
      return displayValue;
    }

    const parsed = parseInt(inputValue, 10);
    return isNaN(parsed) ? 0 : parsed;
  };

  const debounceChange = (newVal: number) => {
    const nextValue = newVal.toString();
    setInputValue(nextValue);
    scheduleClickDebounce(nextValue);
  };

  const handleChange = (displayValueForApply: number) => {
    if (onChange && !disabled) {
      const mm = fromUnitToMm(displayValueForApply, unit) + (mask ?? 0);
      onChange(mm);
    }
  };

  const handleMinusClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const current = getCurrentValue();
    const roundedValue = roundValue(current - stepForInput, stepForInput);
    debounceChange(roundedValue);
  };

  const handlePlusClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const current = getCurrentValue();
    const roundedValue = roundValue(current + stepForInput, stepForInput);
    debounceChange(roundedValue);
  };

  const handleSubMinusClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const current = getCurrentValue();
    const usedStep = subStepForInput ?? stepForInput;
    const roundedValue = roundValue(current - usedStep, usedStep);
    debounceChange(roundedValue);
  };

  const handleSubPlusClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const current = getCurrentValue();
    const usedStep = subStepForInput ?? stepForInput;
    const roundedValue = roundValue(current + usedStep, usedStep);
    debounceChange(roundedValue);
  };

  const handleInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
  };

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.stopPropagation();
    setIsFocused(true);
    setIsNotValid(false);
    setInputValue("");
    scheduleClickDebounce("");
  };

  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    event.stopPropagation();
    setIsFocused(false);

    if (inputValue === "") {
      const nextDisplay = displayValue.toString();
      setInputValue(nextDisplay);
      scheduleClickDebounce(nextDisplay);
      setIsNotValid(false);
    }
  };

  const handleInputValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    scheduleKeyboardDebounce(newValue);
  };

  useEffect(() => {
    const nextDisplay = displayValue.toString();

    if (inputValueRef.current !== nextDisplay) {
      setInputValue(nextDisplay);
      scheduleClickDebounce(nextDisplay);
    }
  }, [unit, value]);

  return (
    <div className={`number-input ${className || ""}`}>
      <div className={`number-input__label number-input${className || ""}`}>
        <div className="start-option-container">
          {image && (
            <img
              src={image}
              alt={label}
              className="number-input__image option-icon-svg"
            />
          )}
          {label && (
            <>
              <div className={"dimension-label"}>{label}</div>
            </>
          )}
        </div>
        {showSubLabel && (
          <span>
            (min. {displayedMin} - max. {displayedMax})
          </span>
        )}
      </div>

      <div className="number-input__wrapper">
        {subStep && (
          <button
            title="Verminderen"
            className="number-input__minus--sub"
            onClick={handleSubMinusClick}
            disabled={atSubMin || disabled || disabledMin}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="icon-plus"
            >
              <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" />
            </svg>
          </button>
        )}
        <button
          title="Verminderen"
          className="number-input__minus"
          onClick={handleMinusClick}
          disabled={atMin || disabled || disabledMin}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="icon-plus"
          >
            <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" />
          </svg>
        </button>
        <div className={`number-input__input ${isNotValid ? "no-valid" : ""}`}>
          <input
            title="Invoer"
            ref={ref}
            type="number"
            value={inputValue}
            placeholder={displayValue.toString()}
            onChange={handleInputValue}
            onClick={handleInputClick}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            min={displayedMin}
            max={displayedMax}
            step={stepForInput}
          />
          <span className="number-input__unit">{unit}</span>
        </div>
        <button
          title="Toenemen"
          className="number-input__plus"
          onClick={handlePlusClick}
          disabled={atMax || disabled || disabledMax}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="icon-plus"
          >
            <line x1="6" y1="0" x2="6" y2="12" stroke="currentColor" />
            <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" />
          </svg>
        </button>
        {subStep && (
          <button
            title="Toenemen"
            className="number-input__plus--sub"
            onClick={handleSubPlusClick}
            disabled={atSubMax || disabled || disabledMax}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="icon-plus"
            >
              <line x1="6" y1="0" x2="6" y2="12" stroke="currentColor" />
              <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};
